import { Link } from 'react-router-dom';
import { colors } from '../../styles/theme';

interface Props {
  /** Short, human label for the feature behind the gate — appears in the
   *  copy so the message reads naturally (e.g. "Payment instructions",
   *  "Trusted contacts"). */
  feature: string;
}

/**
 * Inline gate shown on client pages that require an approved onboarding
 * case (Payment instructions, Contracts, Trusted contacts). Surfaces a
 * direct CTA into the onboarding wizard so a misnavigated/bookmarked
 * user never sees a dead end.
 *
 * Visual treatment mirrors the warning palette used elsewhere
 * (RfcReminderBanner) so the friction is noticed without escalating
 * into a destructive/error tone — the user hasn't done anything wrong;
 * they just aren't here yet.
 */
export default function OnboardingRequiredPanel({
  feature,
}: Readonly<Props>): JSX.Element {
  return (
    <aside
      role="status"
      aria-live="polite"
      className="rounded-2xl p-5 flex items-start justify-between gap-4 flex-wrap"
      style={{
        background: colors.warningBg,
        border: '1px solid #FCD34D',
        color: colors.inkSoft,
      }}
    >
      <div className="min-w-0 flex-1">
        <p
          className="text-xs font-bold uppercase tracking-widest mb-1"
          style={{ color: colors.warning }}
        >
          Onboarding required
        </p>
        <h3 className="text-base font-semibold mb-1" style={{ color: colors.ink }}>
          {feature} is available once your account is approved
        </h3>
        <p className="text-sm" style={{ color: colors.inkSoft }}>
          Finish your onboarding so our compliance team can approve your
          account. {feature} unlocks automatically once that step is done.
        </p>
      </div>
      <Link
        to="/client/onboarding"
        className="inline-flex items-center px-4 py-2 rounded-lg text-sm font-semibold whitespace-nowrap"
        style={{
          background: colors.navy,
          color: colors.gold,
        }}
      >
        Continue onboarding
      </Link>
    </aside>
  );
}
